<template>
  <div class="admission-documents-container">
    <c-loader v-if="isLoading" :size="isMobile ? 79 : 99" />

    <admission-document-step
      ref="step"
      v-show="!isLoading"
      :documents="documents"
      :custom-documents="customDocuments"
      :options="options"
      @submit="onSubmit"
      @edit="onEdit"
      @load-cities="loadCities"
    />
  </div>
</template>

<script>
import { Loadable, MediaQuery } from '@convenia/mixins'
import { AdmissionDocumentStep } from '@convenia/employee-organisms/SystemFields/Documents/Admission'
import { getFileUrl } from '@modules/fileHelpers'
import { mapActions, mapMutations, mapState, mapGetters } from 'vuex'
import { SET_ACTIONS, GET_ACTIONS } from '@content/consts/Documents'
import * as types from '@types'

export default {
  name: 'DocumentsStepContainer',

  compatConfig: { MODE: 3 },

  mixins: [ MediaQuery, Loadable() ],

  components: {
    AdmissionDocumentStep,
  },

  emits: [ 'next', 'previous', 'load', 'verify' ],

  provide () {
    return {
      employeeId: this.employeeId,
      getFileUrl,
    }
  },

  computed: {
    ...mapState({
      customDocuments: state => state.document.customDocuments,
      employeeId: state => state.admission.employeeUuid,
    }),

    ...mapGetters({
      documents: types.DOCUMENT_GET_DOCUMENTS,
    }),

    isCustomDocument () {
      return Object.keys(this.customDocuments)
        .findIndex(id => id === this.active) >= 0
    },

    selected () {
      if (!this.documents[this.active]) return {}

      return this.documents[this.active]
    },

    options () {
      return {
        cities: this.cities,
      }
    },
  },

  data: () => ({
    showModal: true,
    cities: [],
    active: null,
  }),

  beforeUnmount () {
    this.resetDocuments()
  },

  methods: {
    ...mapMutations({
      setFiles: types.DOCUMENT_SET_FILES,
      resetDocuments: types.DOCUMENT_RESET,
    }),

    ...mapActions({
      deleteFile: types.ADMISSION_DELETE_FILE,
      uploadFiles: types.DOCUMENT_UPLOAD_FILES,
      getDocuments: types.DOCUMENT_GET_DOCUMENTS,
      getCustomDocuments: types.DOCUMENT_CUSTOM_DOCUMENTS,
      getSystemFields: types.DOCUMENT_SYSTEM_FIELDS,
      getCities: types.LOCATION_CITIES,
      validateDocuments: types.DOCUMENT_VALIDATE_DOCUMENTS,
    }),

    next () {
      return this.onNext()
    },

    previous () {
      return this.onSave()
    },

    async load () {
      await this.getSystemFields()
      await this.getDocuments()
      await this.getCustomDocuments()

      this.isLoading = false

      await this.verify()

      this.$emit('load')
    },

    async onNext () {
      const { validated, errors } = this.$refs.step?.validate() || {}
      const valid = await this.validateDocuments({ validated, errors })

      if (!valid) return

      this.$emit('next')
    },

    onSave () {
      this.$emit('previous')
    },

    async onEdit (key) {
      this.active = key
    },

    async validate () {
      if (this.isLoading) return false

      const { validated, errors } = this.$refs.step?.validate() || {}

      const valid = await this.validateDocuments({ validated, errors })

      return valid
    },

    async verify () {
      if (this.isLoading) return false

      const { validated, errors } = this.$refs.step?.verify() || {}

      const valid = await this.validateDocuments({ validated, errors })

      this.$emit('verify', valid)

      return valid
    },

    submit: async () => {},

    async onSubmit (event) {
      this.showRemoveFileModal = false

      const { id, key, data, addedFiles, replacedFiles, callback } = event
      const type = this.isCustomDocument ? 'custom_field_value' : key
      const getAction = GET_ACTIONS[type]
      const updateAction = SET_ACTIONS[type]

      const [ err, document ] = await this.$store.dispatch(updateAction, { id, ...data })

      if (err)
        return callback(err)

      await this.deleteFiles(replacedFiles)

      const [ uploadErr ] = await this.uploadFiles({
        id: this.selected.customFieldValueId || document.id,
        type,
        files: addedFiles,
      })

      if (uploadErr)
        return callback(uploadErr)

      await this.$store.dispatch(getAction)
      await this.getSystemFields()
      await this.verify()

      callback()

      this.clear()
    },

    async deleteFiles (files) {
      const { employeeId } = this

      await Promise.all(files.map(
        file => this.deleteFile({ file, employeeId }))
      )
    },

    async loadCities (stateId) {
      if (!stateId) return

      this.cities = await this.getCities(stateId)
    },

    clear () {
      this.active = null
      this.cities = []
    }
  }
}
</script>

<style lang="scss">
.admission-documents-container {
  position: relative;

  & > .c-loader {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%)
  }

  & > .admission-documents-step {
    margin-top: 20px;
  }
}
</style>
